import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  Box,
  TextField,
  TablePagination,
  InputAdornment,
  Stack,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Tariff } from '../types';
import { tariffApi } from '../api';
import { NewTariffDialog } from './NewTariffDialog';
import ToothSpinner from './ToothSpinner';

function TariffList() {
  const [allTariffs, setAllTariffs] = useState<Tariff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [craftOnly, setCraftOnly] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTariffs();
  }, []);

  const filteredTariffs = allTariffs.filter((tariff) => {
    // Hide tariffs not in CraftCMS when toggle is on
    if (craftOnly && tariff.isActive === undefined && tariff.aliasTariff === undefined) return false;
    if (craftOnly && tariff.isActive === null && tariff.aliasTariff === null) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const id = String(tariff.id);
    const insuranceCompany = (tariff.VERSICHERUNG || '').toLowerCase();
    const tariffCode = (tariff.TARIF || '').toLowerCase();
    const combined = `${id} ${insuranceCompany} ${tariffCode}`;
    return combined.includes(query);
  });

  const paginatedTariffs = filteredTariffs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const fetchTariffs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tariffApi.listTariffs();
      setAllTariffs(data.tariffs);
    } catch (err) {
      setError('Failed to load tariffs. Please ensure the API is configured.');
      console.error('Error fetching tariffs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <ToothSpinner />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Tariffs
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {allTariffs.length} tariffs
            </Typography>
            {searchQuery && (
              <>
                <Typography variant="body2" color="text.secondary">·</Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredTariffs.length} matching
                </Typography>
              </>
            )}
          </Stack>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewDialogOpen(true)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          New Tariff
        </Button>
      </Box>

      {/* Search + Filter */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
        <TextField
          fullWidth
          placeholder="Search by ID, insurance company or tariff code..."
          value={searchQuery}
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <FormControlLabel
          control={<Switch checked={craftOnly} onChange={(e) => { setCraftOnly(e.target.checked); setPage(0); }} size="small" />}
          label={<Typography variant="body2" noWrap>Nur CraftCMS Tarife</Typography>}
          sx={{ flexShrink: 0 }}
        />
      </Stack>

      {/* Table */}
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', py: 1.5 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', py: 1.5 }}>Tariff</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', py: 1.5 }}>Insurance Company</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', py: 1.5 }}>Provider</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTariffs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {searchQuery ? 'No tariffs match your search' : 'No tariffs found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedTariffs.map((tariff) => (
                <TableRow
                  key={tariff.id}
                  hover
                  onClick={() => navigate(`/tariff/${tariff.id}`)}
                  sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{tariff.id}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={500}>{tariff.TARIF}</Typography>
                      {tariff.isActive === false && (
                        <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="Inactive" size="small" color="warning" variant="outlined" sx={{ borderRadius: 1.5, height: 20, fontSize: '0.7rem' }} />
                      )}
                      {tariff.enabled === false && (
                        <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="Disabled" size="small" color="warning" variant="outlined" sx={{ borderRadius: 1.5, height: 20, fontSize: '0.7rem' }} />
                      )}
                      {tariff.aliasTariff && (
                        <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label={`Alias → ${tariff.aliasTariff.title}`} size="small" color="warning" variant="outlined" sx={{ borderRadius: 1.5, height: 20, fontSize: '0.7rem' }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="body2">{tariff.VERSICHERUNG}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Chip
                      label={tariff.automationProvider || 'N/A'}
                      size="small"
                      color={tariff.automationProvider === 'Documenso' ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ borderRadius: 1.5, height: 24, fontSize: '0.75rem' }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredTariffs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </TableContainer>

      <NewTariffDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onCreated={(tariffId) => { fetchTariffs(); navigate(`/tariff/${tariffId}`); }}
      />
    </Box>
  );
}

export default TariffList;
